import type { AiUsageEvent, AiUsageRange, AiUsageSummary, Application } from '@owl/shared';
import { type CSSProperties, type ReactNode, useCallback, useMemo, useState } from 'react';
import type { ShellCtx } from '../components/Shell';
import {
  ChartsRow,
  Kpi,
  formatInt,
  formatSeconds,
  formatUsd,
} from '../components/charts/AiUsageBlocks';
import { api } from '../lib/api';
import { useLoader } from '../lib/useLoader';

const RANGES: AiUsageRange[] = ['1h', '6h', '24h', '7d', '30d', 'mtd'];

function formatNullableUsd(n: number | null): string {
  if (n === null) return '—';
  return formatUsd(n);
}

function formatTimestamp(iso: string): string {
  return new Intl.DateTimeFormat('es-ES', {
    timeZone: 'Europe/Madrid',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

type SortKey = 'timestamp' | 'inputTokens' | 'outputTokens' | 'costUsd';

export function AiAppDetail({ ctx, applicationId }: { ctx: ShellCtx; applicationId: string }) {
  const refreshTick = ctx.refreshTick;
  const env = ctx.environment;
  const [range, setRange] = useState<AiUsageRange>('24h');
  const [app, setApp] = useState<Application | null>(null);
  const [summary, setSummary] = useState<AiUsageSummary | null>(null);
  const [events, setEvents] = useState<AiUsageEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('timestamp');
  const [sortDesc, setSortDesc] = useState(true);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [appRes, sumRes, evRes] = await Promise.all([
        api.getApplication(applicationId),
        api.aiUsageSummary({ range, applicationId, environment: env }),
        api.listAiUsageEvents({ range, applicationId, limit: 200 }),
      ]);
      setApp(appRes.application);
      setSummary(sumRes);
      setEvents(evRes.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    }
  }, [applicationId, env, range]);

  useLoader(load, refreshTick);

  const sortedEvents = useMemo(() => {
    const arr = [...events];
    arr.sort((a, b) => {
      let av: number;
      let bv: number;
      if (sortKey === 'timestamp') {
        av = new Date(a.timestamp).getTime();
        bv = new Date(b.timestamp).getTime();
      } else if (sortKey === 'costUsd') {
        av = a.costUsd ?? 0;
        bv = b.costUsd ?? 0;
      } else {
        av = a[sortKey];
        bv = b[sortKey];
      }
      return sortDesc ? bv - av : av - bv;
    });
    return arr;
  }, [events, sortKey, sortDesc]);

  const inputTokensMedian = useMemo<number>(() => {
    if (events.length === 0) return 0;
    const vals = events.map((e) => e.inputTokens).sort((a, b) => a - b);
    const mid = Math.floor(vals.length / 2);
    if (vals.length % 2 === 0) {
      return ((vals[mid - 1] ?? 0) + (vals[mid] ?? 0)) / 2;
    }
    return vals[mid] ?? 0;
  }, [events]);

  const longThreshold = inputTokensMedian > 0 ? inputTokensMedian * 2 : Number.POSITIVE_INFINITY;

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDesc((d) => !d);
    } else {
      setSortKey(key);
      setSortDesc(true);
    }
  }

  function sortIndicator(key: SortKey): string {
    if (sortKey !== key) return '';
    return sortDesc ? ' ▼' : ' ▲';
  }

  const t = summary?.totals;
  const totalCost = t?.costUsd ?? 0;
  const showAudio = (t?.audioSeconds ?? 0) > 0 || (summary?.allTime?.audioSeconds ?? 0) > 0;
  const showCharacters = (t?.characters ?? 0) > 0 || (summary?.allTime?.characters ?? 0) > 0;

  return (
    <div className="view-pad">
      <div className="view-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => ctx.navigate({ name: 'aiusage' })}
              style={{ padding: '4px 10px', fontSize: 12 }}
            >
              ← AI Dashboard
            </button>
            <div className="view-title">{app?.name ?? 'AI app detail'}</div>
          </div>
          <div className="view-sub">
            Per-application AI usage — same dashboard view scoped to this app
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="range-picker">
            {RANGES.map((r) => (
              <button
                type="button"
                key={r}
                className={`range-opt ${range === r ? 'active' : ''}`}
                onClick={() => setRange(r)}
              >
                {r === 'mtd' ? 'MTD' : r}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {summary === null ? (
        <div className="empty-state">Loading…</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {summary.allTime && (
            <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
              <Kpi label="Events · all-time" value={formatInt(summary.allTime.events)} />
              <Kpi label="Input tokens · all-time" value={formatInt(summary.allTime.inputTokens)} />
              {showAudio && (
                <Kpi label="Audio · all-time" value={formatSeconds(summary.allTime.audioSeconds)} />
              )}
              {showCharacters && (
                <Kpi label="Characters · all-time" value={formatInt(summary.allTime.characters)} />
              )}
              <Kpi label="Cost · all-time" value={formatUsd(summary.allTime.costUsd)} />
            </div>
          )}

          <div className="kpi-row">
            <Kpi label="Events" value={formatInt(t?.events ?? 0)} />
            <Kpi
              label="Input tokens"
              value={formatInt(t?.inputTokens ?? 0)}
              delta={
                (t?.cachedInputTokens ?? 0) > 0
                  ? `${formatInt(t?.cachedInputTokens ?? 0)} cached`
                  : undefined
              }
            />
            <Kpi label="Output tokens" value={formatInt(t?.outputTokens ?? 0)} />
            {showAudio && (
              <Kpi label="Audio processed" value={formatSeconds(t?.audioSeconds ?? 0)} />
            )}
            {showCharacters && <Kpi label="Characters" value={formatInt(t?.characters ?? 0)} />}
            <Kpi label="Cost (USD)" value={formatUsd(t?.costUsd ?? 0)} />
          </div>

          <ChartsRow summary={summary} range={range} />

          <div className="table-wrap">
            <div className="view-sub" style={{ padding: '6px 4px 8px' }}>
              By model
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Provider / Model</th>
                  <th>Events</th>
                  <th>Input</th>
                  <th>Output</th>
                  <th>Cost</th>
                  <th>%</th>
                </tr>
              </thead>
              <tbody>
                {summary.byModel.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="dim" style={{ padding: 16, textAlign: 'center' }}>
                      No events in this range
                    </td>
                  </tr>
                ) : (
                  summary.byModel.map((m) => (
                    <tr key={`${m.provider}/${m.model}`}>
                      <td>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: 3,
                            background: m.color,
                            marginRight: 8,
                            verticalAlign: 'middle',
                          }}
                        />
                        <strong>{m.displayLabel}</strong>{' '}
                        <span className="dim" style={{ fontSize: 11 }}>
                          ({m.provider}/{m.model})
                        </span>
                      </td>
                      <td className="mono">{formatInt(m.events)}</td>
                      <td className="mono">{formatInt(m.inputTokens)}</td>
                      <td className="mono">{formatInt(m.outputTokens)}</td>
                      <td className="mono">{formatUsd(m.costUsd)}</td>
                      <td className="mono dim">
                        {totalCost > 0 ? `${((m.costUsd / totalCost) * 100).toFixed(1)}%` : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="table-wrap">
            <div className="view-sub" style={{ padding: '6px 4px 8px' }}>
              Recent events ({events.length}
              {events.length >= 200 ? ' · capped at 200' : ''}) — input tokens &gt;{' '}
              {formatInt(Math.round(longThreshold))} highlighted as long
            </div>
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh sortKey="timestamp" onSort={toggleSort} indicator={sortIndicator}>
                    Timestamp
                  </SortableTh>
                  <th>Provider / Model</th>
                  <SortableTh
                    sortKey="inputTokens"
                    onSort={toggleSort}
                    indicator={sortIndicator}
                    align="right"
                    className="mono"
                  >
                    Input
                  </SortableTh>
                  <SortableTh
                    sortKey="outputTokens"
                    onSort={toggleSort}
                    indicator={sortIndicator}
                    align="right"
                    className="mono"
                  >
                    Output
                  </SortableTh>
                  <SortableTh
                    sortKey="costUsd"
                    onSort={toggleSort}
                    indicator={sortIndicator}
                    align="right"
                    className="mono"
                  >
                    Cost
                  </SortableTh>
                  <th className="dim">Source</th>
                </tr>
              </thead>
              <tbody>
                {sortedEvents.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="dim" style={{ padding: 16, textAlign: 'center' }}>
                      No events in this range
                    </td>
                  </tr>
                ) : (
                  sortedEvents.map((e) => {
                    const isLong = e.inputTokens > longThreshold;
                    return (
                      <tr
                        key={e.id}
                        style={isLong ? { background: 'rgba(212, 177, 92, 0.08)' } : undefined}
                      >
                        <td className="mono" style={{ fontSize: 11.5 }}>
                          {formatTimestamp(e.timestamp)}
                        </td>
                        <td>
                          <strong>{e.provider}</strong>
                          <span className="dim"> / {e.model}</span>
                        </td>
                        <td className="mono" style={{ textAlign: 'right' }}>
                          {formatInt(e.inputTokens)}
                          {isLong && (
                            <span
                              className="dim"
                              style={{ marginLeft: 6, fontSize: 10, color: '#D4B15C' }}
                              title={`> 2× median (${formatInt(Math.round(inputTokensMedian))})`}
                            >
                              long
                            </span>
                          )}
                        </td>
                        <td className="mono" style={{ textAlign: 'right' }}>
                          {formatInt(e.outputTokens)}
                        </td>
                        <td className="mono" style={{ textAlign: 'right' }}>
                          {formatNullableUsd(e.costUsd)}
                        </td>
                        <td className="dim" style={{ fontSize: 11 }}>
                          {e.costSource}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SortableTh({
  sortKey,
  onSort,
  indicator,
  align,
  className,
  children,
}: {
  sortKey: SortKey;
  onSort: (k: SortKey) => void;
  indicator: (k: SortKey) => string;
  align?: 'right';
  className?: string;
  children: ReactNode;
}) {
  const thStyle: CSSProperties = align === 'right' ? { textAlign: 'right' } : {};
  const btnStyle: CSSProperties = {
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0,
    color: 'inherit',
    font: 'inherit',
    cursor: 'pointer',
    textAlign: align === 'right' ? 'right' : 'left',
    width: '100%',
  };
  return (
    <th className={className} style={thStyle}>
      <button type="button" style={btnStyle} onClick={() => onSort(sortKey)}>
        {children}
        {indicator(sortKey)}
      </button>
    </th>
  );
}
